import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  widgetsAPI,
  apiKeysAPI,
  ApiKey,
  Assistant,
  DEFAULT_THEME,
  ThemeConfig,
  CreateAssistantData,
} from '../lib/api';
import {
  ArrowLeft,
  Loader2,
  Plus,
  X,
  AlertCircle,
} from 'lucide-react';

export default function CreateWidgetPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedApiKeyId, setSelectedApiKeyId] = useState('');
  const [selectedAssistantId, setSelectedAssistantId] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState(
    'Hello! How can I help you today?'
  );
  const [placeholderText, setPlaceholderText] = useState('Type your message...');
  const [domains, setDomains] = useState<string[]>(['']);
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);

  // Data
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(true);
  const [isLoadingAssistants, setIsLoadingAssistants] = useState(false);

  // Create assistant modal state
  const [showCreateAssistant, setShowCreateAssistant] = useState(false);
  const [isCreatingAssistant, setIsCreatingAssistant] = useState(false);
  const [newAssistant, setNewAssistant] = useState<CreateAssistantData>({
    name: '',
    instructions: 'You are a helpful assistant.',
    model: 'gpt-4o-mini',
    description: '',
  });

  useEffect(() => {
    loadApiKeys();
  }, []);

  useEffect(() => {
    if (selectedApiKeyId) {
      loadAssistants(selectedApiKeyId);
    } else {
      setAssistants([]);
      setSelectedAssistantId('');
    }
  }, [selectedApiKeyId]);

  const loadApiKeys = async () => {
    try {
      const data = await apiKeysAPI.list();
      setApiKeys(data.api_keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const loadAssistants = async (keyId: string) => {
    setIsLoadingAssistants(true);
    try {
      const data = await apiKeysAPI.listAssistants(keyId);
      setAssistants(data.assistants);
    } catch (error) {
      console.error('Failed to load assistants:', error);
      setAssistants([]);
    } finally {
      setIsLoadingAssistants(false);
    }
  };

  const handleCreateAssistant = async () => {
    if (!newAssistant.name.trim() || !selectedApiKeyId) return;

    setIsCreatingAssistant(true);
    try {
      const result = await apiKeysAPI.createAssistant(selectedApiKeyId, newAssistant);
      // Add to list and select it
      setAssistants([result.assistant, ...assistants]);
      setSelectedAssistantId(result.assistant.id);
      setShowCreateAssistant(false);
      setNewAssistant({
        name: '',
        instructions: 'You are a helpful assistant.',
        model: 'gpt-4o-mini',
        description: '',
      });
    } catch (error) {
      console.error('Failed to create assistant:', error);
      setError(error instanceof Error ? error.message : 'Failed to create assistant');
    } finally {
      setIsCreatingAssistant(false);
    }
  };

  const addDomain = () => {
    setDomains([...domains, '']);
  };

  const removeDomain = (index: number) => {
    if (domains.length === 1) return;
    setDomains(domains.filter((_, i) => i !== index));
  };

  const updateDomain = (index: number, value: string) => {
    const newDomains = [...domains];
    newDomains[index] = value;
    setDomains(newDomains);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Widget name is required');
      return;
    }

    if (!selectedApiKeyId) {
      setError('Please select an API key');
      return;
    }

    if (!selectedAssistantId) {
      setError('Please select an assistant');
      return;
    }

    const validDomains = domains.filter((d) => d.trim());
    if (validDomains.length === 0) {
      setError('At least one domain is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await widgetsAPI.create({
        name: name.trim(),
        description: description.trim() || undefined,
        api_key_id: selectedApiKeyId,
        assistant_id: selectedAssistantId,
        welcome_message: welcomeMessage,
        placeholder_text: placeholderText,
        allowed_domains: validDomains,
        theme_config: theme,
      });

      navigate(`/widgets/${result.widget.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create widget');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/widgets')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Widgets
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Widget</h1>
        <p className="text-gray-600 mt-1">
          Set up a new chat widget for your website
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Widget Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="My Chat Widget"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                placeholder="Optional description for your widget"
              />
            </div>
          </div>
        </div>

        {/* OpenAI Configuration */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            OpenAI Configuration
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key *
              </label>
              {isLoadingKeys ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading API keys...
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    No API keys found. Add an API key first.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/api-keys')}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Go to API Keys →
                  </button>
                </div>
              ) : (
                <select
                  value={selectedApiKeyId}
                  onChange={(e) => setSelectedApiKeyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                >
                  <option value="">Select an API key</option>
                  {apiKeys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.name} ({key.key_preview})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assistant *
              </label>
              {!selectedApiKeyId ? (
                <p className="text-sm text-gray-500">Select an API key first</p>
              ) : isLoadingAssistants ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading assistants...
                </div>
              ) : (
                <div className="space-y-3">
                  <select
                    value={selectedAssistantId}
                    onChange={(e) => setSelectedAssistantId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="">Select an assistant</option>
                    {assistants.map((assistant) => (
                      <option key={assistant.id} value={assistant.id}>
                        {assistant.name || assistant.id} ({assistant.model})
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => setShowCreateAssistant(true)}
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Create New Assistant
                  </button>
                </div>
              )}
            </div>

            {/* Create Assistant Modal */}
            {showCreateAssistant && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Create New Assistant</h3>
                    <button
                      type="button"
                      onClick={() => setShowCreateAssistant(false)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={newAssistant.name}
                        onChange={(e) => setNewAssistant({ ...newAssistant, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="My Assistant"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model
                      </label>
                      <select
                        value={newAssistant.model}
                        onChange={(e) => setNewAssistant({ ...newAssistant, model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                      >
                        <option value="gpt-4o-mini">GPT-4o Mini (Fast & Affordable)</option>
                        <option value="gpt-4o">GPT-4o (Best Quality)</option>
                        <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fastest)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Instructions
                      </label>
                      <textarea
                        value={newAssistant.instructions}
                        onChange={(e) => setNewAssistant({ ...newAssistant, instructions: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                        placeholder="You are a helpful assistant..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Instructions tell the assistant how to behave and respond.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        value={newAssistant.description}
                        onChange={(e) => setNewAssistant({ ...newAssistant, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="A brief description of what this assistant does"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowCreateAssistant(false)}
                      className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateAssistant}
                      disabled={isCreatingAssistant || !newAssistant.name.trim()}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreatingAssistant && <Loader2 className="w-4 h-4 animate-spin" />}
                      Create Assistant
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Allowed Domains */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Allowed Domains *
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            The widget will only load on these domains. Use *.example.com for
            wildcards.
          </p>

          <div className="space-y-3">
            {domains.map((domain, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => updateDomain(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="example.com"
                />
                {domains.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDomain(index)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addDomain}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Domain
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Messages</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Welcome Message
              </label>
              <input
                type="text"
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Input Placeholder
              </label>
              <input
                type="text"
                value={placeholderText}
                onChange={(e) => setPlaceholderText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Embed Mode */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Embed Mode
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose how the widget appears on your website
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              onClick={() => setTheme({ ...theme, embedMode: 'popup' })}
              className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                theme.embedMode === 'popup'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <span className="font-medium text-gray-900">Popup Widget</span>
              </div>
              <p className="text-sm text-gray-600">
                Floating button in the corner that opens a chat window. Great for customer support.
              </p>
            </div>

            <div
              onClick={() => setTheme({ ...theme, embedMode: 'inline' })}
              className={`relative flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${
                theme.embedMode === 'inline'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <span className="font-medium text-gray-900">Inline Embed</span>
              </div>
              <p className="text-sm text-gray-600">
                Full-size chat embedded directly in your page. Perfect for dedicated chat pages.
              </p>
            </div>
          </div>

          {/* Inline Size Options */}
          {theme.embedMode === 'inline' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Inline Embed Size</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Width</label>
                  <input
                    type="text"
                    value={theme.inlineWidth}
                    onChange={(e) => setTheme({ ...theme, inlineWidth: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                    placeholder="100%"
                  />
                  <p className="text-xs text-gray-400 mt-1">e.g., 100%, 800px, 50vw</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Height</label>
                  <input
                    type="text"
                    value={theme.inlineHeight}
                    onChange={(e) => setTheme({ ...theme, inlineHeight: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                    placeholder="600px"
                  />
                  <p className="text-xs text-gray-400 mt-1">e.g., 600px, 100vh, 80%</p>
                </div>
              </div>
            </div>
          )}

          {/* Popup Position Options */}
          {theme.embedMode === 'popup' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Button Position</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Corner</label>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Top Left */}
                    <div
                      onClick={() => setTheme({ ...theme, buttonPosition: 'top-left' })}
                      className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                        theme.buttonPosition === 'top-left'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                        <div className={`absolute top-0.5 left-0.5 w-2 h-2 rounded-full ${theme.buttonPosition === 'top-left' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                      </div>
                      <span className={`text-sm ${theme.buttonPosition === 'top-left' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Top Left</span>
                    </div>
                    {/* Top Right */}
                    <div
                      onClick={() => setTheme({ ...theme, buttonPosition: 'top-right' })}
                      className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                        theme.buttonPosition === 'top-right'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                        <div className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full ${theme.buttonPosition === 'top-right' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                      </div>
                      <span className={`text-sm ${theme.buttonPosition === 'top-right' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Top Right</span>
                    </div>
                    {/* Bottom Left */}
                    <div
                      onClick={() => setTheme({ ...theme, buttonPosition: 'bottom-left' })}
                      className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                        theme.buttonPosition === 'bottom-left'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                        <div className={`absolute bottom-0.5 left-0.5 w-2 h-2 rounded-full ${theme.buttonPosition === 'bottom-left' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                      </div>
                      <span className={`text-sm ${theme.buttonPosition === 'bottom-left' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Bottom Left</span>
                    </div>
                    {/* Bottom Right */}
                    <div
                      onClick={() => setTheme({ ...theme, buttonPosition: 'bottom-right' })}
                      className={`p-3 rounded-lg cursor-pointer border-2 transition-all flex items-center gap-2 ${
                        theme.buttonPosition === 'bottom-right'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-8 h-8 border-2 border-gray-300 rounded relative bg-white">
                        <div className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${theme.buttonPosition === 'bottom-right' ? 'bg-primary-500' : 'bg-gray-400'}`}></div>
                      </div>
                      <span className={`text-sm ${theme.buttonPosition === 'bottom-right' ? 'text-primary-700 font-medium' : 'text-gray-600'}`}>Bottom Right</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Horizontal Offset (px)</label>
                    <input
                      type="number"
                      value={theme.buttonOffsetX}
                      onChange={(e) => setTheme({ ...theme, buttonOffsetX: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Vertical Offset (px)</label>
                    <input
                      type="number"
                      value={theme.buttonOffsetY}
                      onChange={(e) => setTheme({ ...theme, buttonOffsetY: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Theme Colors */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Theme Colors
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.primaryColor}
                  onChange={(e) =>
                    setTheme({ ...theme, primaryColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.primaryColor}
                  onChange={(e) =>
                    setTheme({ ...theme, primaryColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Header Background
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.headerBackgroundColor}
                  onChange={(e) =>
                    setTheme({ ...theme, headerBackgroundColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.headerBackgroundColor}
                  onChange={(e) =>
                    setTheme({ ...theme, headerBackgroundColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Button Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.buttonColor}
                  onChange={(e) =>
                    setTheme({ ...theme, buttonColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.buttonColor}
                  onChange={(e) =>
                    setTheme({ ...theme, buttonColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Bubble
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.userBubbleColor}
                  onChange={(e) =>
                    setTheme({ ...theme, userBubbleColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.userBubbleColor}
                  onChange={(e) =>
                    setTheme({ ...theme, userBubbleColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assistant Bubble
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.assistantBubbleColor}
                  onChange={(e) =>
                    setTheme({ ...theme, assistantBubbleColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.assistantBubbleColor}
                  onChange={(e) =>
                    setTheme({ ...theme, assistantBubbleColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Background
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={theme.backgroundColor}
                  onChange={(e) =>
                    setTheme({ ...theme, backgroundColor: e.target.value })
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={theme.backgroundColor}
                  onChange={(e) =>
                    setTheme({ ...theme, backgroundColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/widgets')}
            className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Widget
          </button>
        </div>
      </form>
    </div>
  );
}
